import React, { useEffect, useState } from 'react';
import {
  Menu,
  X,
  MessageCircle,
  MapPin,
  Clock,
  Users,
  Euro,
  Wallet,
  Package,
  ArrowUpRight,
  ArrowDown,
  CalendarDays,
} from 'lucide-react';

/* --------------------------------------------------------------------------
   Contenu éditable
   -------------------------------------------------------------------------- */

const PHONE_DISPLAY = '06 13 77 80 65';
const PHONE_INTL = '33613778065';
const WHATSAPP_URL = `https://wa.me/${PHONE_INTL}?text=${encodeURIComponent(
  'Bonjour Nicolas, je souhaite réserver une place pour un prochain atelier.'
)}`;
const SMS_URL = `sms:+${PHONE_INTL}`;
const WHATSAPP_GROUP_URL = 'https://chat.whatsapp.com/EVQ3CH3sCtcBkpzuet64g6';
const FACEBOOK_URL = 'https://facebook.com/beyourzenself';
const SKOOL_URL = 'https://www.skool.com/elastic-warrior-1271/about';
const PRIMELIGHT_URL = 'https://www.nicolasleberre.com/p/prime-light-experience';
const LIEU_URL = 'https://linktr.ee/Parenthese_Holistique';
const MAPS_URL =
  'https://www.google.com/maps/search/?api=1&query=13B+rue+de+l%27%C3%89glise+22400+Saint-Alban';

/** Prochains ateliers : les dates passées sont masquées automatiquement. */
const UPCOMING: { date: string; label: string }[] = [
  { date: '2026-09-15', label: 'Yoga pour Arts Martiaux' },
  { date: '2026-09-22', label: 'Méditation' },
  { date: '2026-09-29', label: 'Yoga pour Arts Martiaux' },
  { date: '2026-10-06', label: 'Yoga pour Arts Martiaux' },
  { date: '2026-10-13', label: 'Yoga pour Arts Martiaux' },
];

const NAV = [
  { href: '#yoga', label: 'Yoga' },
  { href: '#meditation', label: 'Méditation' },
  { href: '#infos', label: 'Infos pratiques' },
  { href: '#nicolas', label: 'Nicolas' },
];

/* --------------------------------------------------------------------------
   Hooks
   -------------------------------------------------------------------------- */

function useReveal() {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'));
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

function useScrolled(offset = 40) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > offset);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [offset]);
  return scrolled;
}

/* --------------------------------------------------------------------------
   Petits composants
   -------------------------------------------------------------------------- */

const btnBase =
  'inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-medium tracking-wide transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2';
const btnInk = `${btnBase} bg-ink text-paper hover:bg-sage-600`;
const btnPaper = `${btnBase} bg-paper text-ink hover:bg-sage-100`;
const btnGhostPaper = `${btnBase} border border-paper/60 text-paper hover:bg-paper hover:text-ink`;
const btnGhostInk = `${btnBase} border border-ink/30 text-ink hover:border-ink hover:bg-ink hover:text-paper`;

const Eyebrow: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <p className={`eyebrow ${className}`}>{children}</p>
);

const SectionHeading: React.FC<{
  index: string;
  eyebrow: string;
  title: React.ReactNode;
  tone?: 'ink' | 'paper';
}> = ({ index, eyebrow, title, tone = 'ink' }) => (
  <div data-reveal className="max-w-3xl">
    <div className={`flex items-center gap-4 ${tone === 'ink' ? 'text-sage-600' : 'text-sage-200'}`}>
      <span className="font-display text-lg italic">{index}</span>
      <span className="hairline w-10" />
      <Eyebrow>{eyebrow}</Eyebrow>
    </div>
    <h2 className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
      {title}
    </h2>
  </div>
);

const ExternalLink: React.FC<{ href: string; children: React.ReactNode; className?: string }> = ({
  href,
  children,
  className = '',
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`underline decoration-1 underline-offset-4 decoration-sage hover:decoration-current transition-colors ${className}`}
  >
    {children}
  </a>
);

const formatDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

/* --------------------------------------------------------------------------
   Page
   -------------------------------------------------------------------------- */

const LandingPage: React.FC = () => {
  useReveal();
  const scrolled = useScrolled();
  const [menuOpen, setMenuOpen] = useState(false);

  // Accès direct avec une ancre (ex. /#infos) : le DOM n'existe pas encore
  // quand le navigateur traite le hash, on rejoue le scroll après le montage.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcoming = UPCOMING.filter((w) => new Date(`${w.date}T12:00:00`) >= today).slice(0, 4);

  return (
    <div className="landing min-h-screen overflow-x-clip">
      {/* ------------------------------------------------------------------ */}
      {/* Navigation                                                          */}
      {/* ------------------------------------------------------------------ */}
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled || menuOpen ? 'bg-paper/90 backdrop-blur-md text-ink shadow-[0_1px_0_0_rgba(27,30,27,0.08)]' : 'text-paper'
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 py-4">
          <a href="#top" className="flex items-center gap-3 group" aria-label="Retour en haut">
            <img
              src="/landing/logo-enso.png"
              alt=""
              className={`h-9 w-9 object-contain transition-all duration-500 ${
                scrolled || menuOpen ? '' : 'brightness-0 invert'
              }`}
            />
            <span className="font-display text-xl sm:text-2xl leading-none tracking-tight">
              Nicolas Le Berre
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-10">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="text-[13px] font-medium tracking-[0.18em] uppercase opacity-80 hover:opacity-100 transition-opacity"
              >
                {item.label}
              </a>
            ))}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`${btnBase} px-5 py-2.5 ${
                scrolled ? 'bg-ink text-paper hover:bg-sage-600' : 'bg-paper text-ink hover:bg-sage-100'
              }`}
            >
              <MessageCircle size={16} strokeWidth={1.75} />
              Réserver
            </a>
          </nav>

          <button
            type="button"
            className="md:hidden p-2 -mr-2"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={26} strokeWidth={1.5} /> : <Menu size={26} strokeWidth={1.5} />}
          </button>
        </div>

        {menuOpen && (
          <div className="md:hidden absolute top-full inset-x-0 h-[calc(100svh-68px)] overflow-y-auto bg-paper text-ink border-t border-ink/10">
            <nav className="flex h-full flex-col px-8 pt-10">
              {NAV.map((item, i) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setMenuOpen(false)}
                  className="font-display text-4xl font-light py-4 border-b border-ink/10"
                  style={{ transitionDelay: `${i * 40}ms` }}
                >
                  {item.label}
                </a>
              ))}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`${btnInk} mt-10`}
                onClick={() => setMenuOpen(false)}
              >
                <MessageCircle size={16} strokeWidth={1.75} />
                Réserver par WhatsApp
              </a>
              <p className="mt-auto pb-10 text-sm text-stone">
                Parenthèse Holistique · 13 B rue de l'Église · Saint-Alban
              </p>
            </nav>
          </div>
        )}
      </header>

      {/* ------------------------------------------------------------------ */}
      {/* Hero                                                                */}
      {/* ------------------------------------------------------------------ */}
      <section id="top" className="relative min-h-[100svh] flex items-end bg-ink text-paper">
        <img
          src="/landing/hero-virabhadrasana.jpg"
          alt="Nicolas Le Berre en posture de yoga, guerrier inversé"
          className="absolute inset-0 h-full w-full object-cover object-[60%_center] grayscale"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-ink/20 to-transparent" />

        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 pb-16 sm:pb-24 pt-40">
          <div className="flex items-start gap-6">
            <span className="hero-line hidden sm:block w-px h-40 bg-paper/50 mt-2" aria-hidden="true" />
            <div className="max-w-3xl">
              <Eyebrow className="text-sage-200">
                Parenthèse Holistique · Saint-Alban{'\u00a0'}(22)<span className="hidden sm:inline"> · Le mardi soir</span>
              </Eyebrow>
              <h1 className="mt-6 font-display font-light text-[2.75rem] leading-[1.02] sm:text-6xl lg:text-7xl xl:text-[5.5rem] tracking-tight">
                Souplesse, mobilité,
                <br />
                <em className="not-italic text-sage-200">liberté</em> de mouvement.
              </h1>
              <p className="mt-8 max-w-xl text-base sm:text-lg text-paper/80 leading-relaxed">
                Ateliers de <strong className="font-medium text-paper">Yoga pour Arts Martiaux</strong> et de{' '}
                <strong className="font-medium text-paper">Méditation</strong> avec Nicolas Le Berre. Un corps plus
                libre et un esprit plus clair, au service de votre pratique et de votre quotidien.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={btnPaper}>
                  <MessageCircle size={16} strokeWidth={1.75} />
                  Réserver par WhatsApp
                </a>
                <a href="#yoga" className={btnGhostPaper}>
                  Découvrir les ateliers
                  <ArrowDown size={16} strokeWidth={1.75} />
                </a>
              </div>
            </div>
          </div>

          <div className="mt-16 sm:mt-24 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-paper/15 pt-6 text-sm text-paper/70">
            <div>
              <span className="block font-display text-2xl text-paper">Mardi</span>19h – 20h30
            </div>
            <div>
              <span className="block font-display text-2xl text-paper">5 pers.</span>maximum par atelier
            </div>
            <div>
              <span className="block font-display text-2xl text-paper">2 / mois</span>de septembre à juin
            </div>
            <div>
              <span className="block font-display text-2xl text-paper">Tout niveau</span>matériel fourni
            </div>
          </div>
        </div>

        <span
          className="absolute right-6 sm:right-10 top-1/2 -translate-y-1/2 hidden lg:block kanji text-paper/25 text-2xl tracking-[0.5em] [writing-mode:vertical-rl]"
          aria-hidden="true"
        >
          武道 · 瑜伽 · 禅
        </span>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Manifeste                                                           */}
      {/* ------------------------------------------------------------------ */}
      <section className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-36">
        <span className="enso -top-10 -left-24 h-[28rem] w-[28rem] text-sage" aria-hidden="true" />
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8">
          <div className="lg:col-span-7" data-reveal>
            <p className="font-display font-light text-3xl sm:text-4xl lg:text-[2.9rem] leading-[1.2] tracking-tight">
              Inspiré de la tradition de l'<em>Ashtanga Yoga</em> et des principes fondamentaux des{' '}
              <em>Budō</em> japonais, ce travail rend au corps son amplitude et à l'esprit sa clarté.
            </p>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 space-y-6 text-[15px] leading-relaxed text-ink/75" data-reveal style={{ ['--reveal-delay' as string]: '150ms' }}>
            <p>
              Des séances en petit groupe de cinq personnes au plus, pour un accompagnement précis et
              individualisé. Chaque atelier alterne respiration, mobilité, postures d'ouverture, force et
              retour au calme.
            </p>
            <p>
              Deux propositions se répondent : un <strong className="font-medium text-ink">yoga régulier</strong> pensé
              pour les pratiquants d'arts martiaux et de sport, et des{' '}
              <strong className="font-medium text-ink">ateliers ponctuels de méditation</strong> ouverts à tous.
            </p>
            <a href="#infos" className="inline-flex items-center gap-2 text-sm font-medium tracking-wide text-sage-600 hover:text-ink transition-colors">
              Infos pratiques & tarifs <ArrowDown size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* 01 — Yoga pour Arts Martiaux                                        */}
      {/* ------------------------------------------------------------------ */}
      <section id="yoga" className="scroll-mt-20 border-t border-ink/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
          <SectionHeading
            index="01"
            eyebrow="Ateliers réguliers · le mardi soir"
            title={
              <>
                Yoga pour
                <br />
                Arts Martiaux
              </>
            }
          />

          <div className="mt-14 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5 space-y-6 text-[15px] leading-relaxed text-ink/75" data-reveal>
              <p className="font-display text-2xl leading-snug text-ink">
                Un corps plus libre, au service de votre pratique sportive ou martiale, et de votre quotidien.
              </p>
              <p>
                Enrichissez votre pratique martiale ou sportive par des séances spécialement conçues pour gagner en
                amplitude, en fluidité et en force. Le travail s'adresse aux karatékas, judokas, boxeurs, pratiquants
                d'arts martiaux et sportifs de toutes disciplines, mais aussi à toute personne qui souhaite retrouver de
                l'aisance dans son corps.
              </p>
              <p>
                Aucun niveau de souplesse préalable n'est requis. Tout le matériel (tapis, blocs, sangles) est fourni
                sur place.
              </p>
            </div>

            <ol className="lg:col-span-6 lg:col-start-7 divide-y divide-ink/10 border-y border-ink/10">
              {[
                {
                  n: 'I',
                  title: 'Souplesse & mobilité',
                  text: 'Pour plus de fluidité, de meilleurs coups de pied et une plus grande disponibilité du corps.',
                },
                {
                  n: 'II',
                  title: 'Force & élasticité',
                  text: "Pour plus d'ancrage, de stabilité et de dynamisme dans chaque appui et chaque déplacement.",
                },
                {
                  n: 'III',
                  title: 'Concentration & énergie interne',
                  text: 'Au travers de pratiques méditatives et respiratoires spécifiques, héritées du yoga et des Budō.',
                },
              ].map((item, i) => (
                <li
                  key={item.n}
                  className="grid grid-cols-[3rem_1fr] gap-4 py-7"
                  data-reveal
                  style={{ ['--reveal-delay' as string]: `${i * 120}ms` }}
                >
                  <span className="font-display text-2xl italic text-sage-600">{item.n}</span>
                  <div>
                    <h3 className="font-display text-2xl sm:text-[1.75rem] leading-tight">{item.title}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-ink/70">{item.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Galerie */}
        <div className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 sm:pb-32">
          <div className="grid grid-cols-12 gap-3 sm:gap-5">
            <figure className="col-span-12 sm:col-span-7 group" data-reveal>
              <div className="overflow-hidden bg-sage-100 aspect-[3/2]">
                <img
                  src="/landing/cossack-bw.jpg"
                  alt="Posture de squat cosaque profond"
                  loading="lazy"
                  className="h-full w-full object-cover grayscale transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
                />
              </div>
              <figcaption className="mt-3 eyebrow text-stone">Squat cosaque · ouverture des hanches</figcaption>
            </figure>
            <figure className="col-span-12 sm:col-span-5 group flex flex-col" data-reveal style={{ ['--reveal-delay' as string]: '100ms' }}>
              <div className="overflow-hidden bg-sage-100 aspect-[3/2] sm:aspect-auto sm:flex-1 sm:min-h-0">
                <img
                  src="/landing/straddle-split.jpg"
                  alt="Grand écart facial, mains jointes dans le dos"
                  loading="lazy"
                  className="h-full w-full object-cover grayscale transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
                />
              </div>
              <figcaption className="mt-3 eyebrow text-stone">Écart facial · Namaskar</figcaption>
            </figure>
            <figure className="col-span-6 sm:col-span-4 group" data-reveal>
              <div className="overflow-hidden bg-sage-100 aspect-[4/5]">
                <img
                  src="/landing/back-bending-bw.jpg"
                  alt="Flexion arrière, pied saisi derrière la tête"
                  loading="lazy"
                  className="h-full w-full object-cover object-[45%_center] grayscale transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
                />
              </div>
              <figcaption className="mt-3 eyebrow text-stone">Eka Pada Rajakapotasana</figcaption>
            </figure>
            <figure className="col-span-6 sm:col-span-4 group" data-reveal style={{ ['--reveal-delay' as string]: '100ms' }}>
              <div className="overflow-hidden bg-sage-100 aspect-[4/5]">
                <img
                  src="/landing/bakasana.jpg"
                  alt="Posture du corbeau, équilibre sur les mains"
                  loading="lazy"
                  className="h-full w-full object-cover object-[40%_center] grayscale transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
                />
              </div>
              <figcaption className="mt-3 eyebrow text-stone">Bakasana · force & équilibre</figcaption>
            </figure>
            <figure className="col-span-12 sm:col-span-4 group" data-reveal style={{ ['--reveal-delay' as string]: '200ms' }}>
              <div className="overflow-hidden bg-sage-100 aspect-[3/2] sm:aspect-[4/5]">
                <img
                  src="/landing/kick-dojo-bw.jpg"
                  alt="Coup de pied latéral en karategi"
                  loading="lazy"
                  className="h-full w-full object-cover object-[62%_center] grayscale transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
                />
              </div>
              <figcaption className="mt-3 eyebrow text-stone">Yoko geri kekomi · la finalité martiale</figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* 02 — Méditation                                                     */}
      {/* ------------------------------------------------------------------ */}
      <section id="meditation" className="scroll-mt-20 relative bg-ink text-paper overflow-hidden">
        <span className="enso -bottom-40 -right-40 h-[36rem] w-[36rem] text-paper" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
          <SectionHeading
            index="02"
            eyebrow="Ateliers ponctuels · ouverts à tous"
            title={
              <>
                Méditation
                <br />
                <span className="text-sage-200">PrimeLight</span>
              </>
            }
            tone="paper"
          />

          <div className="mt-16 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <figure className="lg:col-span-6 relative" data-reveal>
              <div className="overflow-hidden aspect-[4/3]">
                <img
                  src="/landing/meditation-sunset.jpg"
                  alt="Nicolas en méditation face à la mer au coucher du soleil"
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
              <figcaption className="mt-3 eyebrow text-paper/40">Côtes d'Armor · retour au silence</figcaption>
            </figure>

            <div className="lg:col-span-5 lg:col-start-8 space-y-6 text-[15px] leading-relaxed text-paper/75" data-reveal style={{ ['--reveal-delay' as string]: '150ms' }}>
              <p className="font-display text-2xl sm:text-[1.75rem] leading-snug text-paper">
                Calmez l'agitation du mental et retrouvez clarté et concentration au quotidien.
              </p>
              <p>
                <ExternalLink href={SKOOL_URL} className="text-paper">Nicolas Le Berre</ExternalLink>, coach formé
                et expérimenté, vous accompagne sur le chemin de votre développement intérieur, notamment au travers de la{' '}
                <ExternalLink href={PRIMELIGHT_URL} className="text-paper">méditation PrimeLight</ExternalLink>.
              </p>
              <p>
                L'intention : vous transmettre petit à petit les clefs d'une pratique quotidienne, en autonomie, qui
                vous corresponde vraiment. Apaiser le stress, se libérer de ses croyances limitantes et se reconnecter
                à sa vraie nature.
              </p>
              <p>
                Ces ateliers sont ouverts à tous, sans prérequis. Ponctuellement, d'autres formats viennent prendre soin
                du corps et de l'esprit autrement : journaling, routines matinales, et bien plus.
              </p>
              <a href={WHATSAPP_GROUP_URL} target="_blank" rel="noopener noreferrer" className={`${btnGhostPaper} mt-2`}>
                Être informé des prochaines dates
                <ArrowUpRight size={16} strokeWidth={1.75} />
              </a>
            </div>
          </div>

          <blockquote className="mt-24 sm:mt-32 max-w-3xl mx-auto text-center" data-reveal>
            <p className="font-display font-light italic text-3xl sm:text-4xl leading-snug text-paper/90">
              « Le plus grand des voyages commence par un premier pas. »
            </p>
            <footer className="mt-5 eyebrow text-sage-200">Lao Tseu</footer>
          </blockquote>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* 03 — Infos pratiques                                                */}
      {/* ------------------------------------------------------------------ */}
      <section id="infos" className="scroll-mt-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
          <SectionHeading index="03" eyebrow="Organisation · tarifs · réservation" title="Infos pratiques" />

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {[
              {
                icon: Clock,
                title: 'Quand',
                lines: ['Le mardi soir, de 19h à 20h30', 'Deux ateliers par mois, de septembre à juin'],
              },
              {
                icon: MapPin,
                title: 'Où',
                lines: ['Parenthèse Holistique', '13 B rue de l’Église, 22400 Saint-Alban'],
                link: { href: MAPS_URL, label: 'Itinéraire' },
              },
              {
                icon: Users,
                title: 'Petits groupes',
                lines: ['5 personnes maximum', 'Pour un meilleur accompagnement'],
              },
              {
                icon: Euro,
                title: 'Tarifs',
                lines: ['12 € la séance', '190 € la saison (septembre à juin)'],
              },
              {
                icon: Wallet,
                title: 'Règlement',
                lines: ['Chèque, espèces ou Wero', 'Sur place, le jour de l’atelier'],
              },
              {
                icon: Package,
                title: 'Matériel',
                lines: ['Tapis, blocs et sangles fournis', 'Venez en tenue souple et confortable'],
              },
            ].map((item, i) => (
              <div
                key={item.title}
                className="border-t border-ink/15 pt-6"
                data-reveal
                style={{ ['--reveal-delay' as string]: `${(i % 3) * 100}ms` }}
              >
                <item.icon size={20} strokeWidth={1.5} className="text-sage-600" />
                <h3 className="mt-4 font-display text-2xl">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-ink/70">
                  {item.lines[0]}
                  <br />
                  {item.lines[1]}
                </p>
                {item.link && (
                  <a
                    href={item.link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-sage-600 hover:text-ink transition-colors"
                  >
                    {item.link.label} <ArrowUpRight size={14} />
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* Réservation + prochaines dates */}
          <div className="mt-20 grid lg:grid-cols-12 gap-10 bg-sage-100 p-8 sm:p-12" data-reveal>
            <div className="lg:col-span-6">
              <Eyebrow className="text-sage-600">Réservation obligatoire</Eyebrow>
              <h3 className="mt-4 font-display font-light text-3xl sm:text-4xl leading-tight">
                Les places sont limitées : réservez la vôtre par SMS ou WhatsApp.
              </h3>
              <p className="mt-4 text-[15px] leading-relaxed text-ink/70">
                Un simple message avec la date souhaitée suffit. Nicolas vous confirme votre place et vous
                communique les dernières informations utiles.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={btnInk}>
                  <MessageCircle size={16} strokeWidth={1.75} />
                  WhatsApp
                </a>
                <a href={SMS_URL} className={btnGhostInk}>
                  SMS au {PHONE_DISPLAY}
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 lg:col-start-8">
              <div className="flex items-center gap-3 text-sage-600">
                <CalendarDays size={18} strokeWidth={1.5} />
                <Eyebrow>Prochains ateliers</Eyebrow>
              </div>
              {upcoming.length > 0 ? (
                <ul className="mt-4 divide-y divide-ink/10 border-y border-ink/10">
                  {upcoming.map((w) => (
                    <li key={w.date} className="flex items-baseline justify-between gap-4 py-4">
                      <span className="font-display text-xl first-letter:uppercase">{formatDate(w.date)}</span>
                      <span className="text-sm text-ink/70 text-right">{w.label}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-4 text-[15px] text-ink/70">
                  Les prochaines dates sont annoncées dans le groupe WhatsApp.
                </p>
              )}
              <a
                href={WHATSAPP_GROUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-sage-600 hover:text-ink transition-colors"
              >
                Rejoindre le groupe WhatsApp pour suivre les dates <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Le lieu                                                             */}
      {/* ------------------------------------------------------------------ */}
      <section id="lieu" className="scroll-mt-20 border-t border-ink/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-3 flex md:justify-start" data-reveal>
            <img
              src="/landing/parenthese-holistique.svg"
              alt="Logo Parenthèse Holistique"
              className="h-40 w-40 sm:h-48 sm:w-48 object-contain"
              loading="lazy"
            />
          </div>
          <div className="md:col-span-8 md:col-start-5" data-reveal style={{ ['--reveal-delay' as string]: '120ms' }}>
            <Eyebrow className="text-sage-600">Le lieu</Eyebrow>
            <h3 className="mt-3 font-display font-light text-3xl sm:text-4xl leading-tight">
              Parenthèse Holistique, un espace paisible au cœur de Saint-Alban.
            </h3>
            <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-ink/70">
              Les séances collectives se déroulent au sein de{' '}
              <ExternalLink href={LIEU_URL}>Parenthèse Holistique</ExternalLink>, un espace harmonieux conçu pour
              favoriser la détente et la concentration. Un lieu à taille humaine, propice à la pratique et à
              l'introspection.
            </p>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* 04 — Nicolas                                                        */}
      {/* ------------------------------------------------------------------ */}
      <section id="nicolas" className="scroll-mt-20 bg-paper-200/60 border-t border-ink/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <figure className="lg:col-span-5 relative" data-reveal>
            <div className="overflow-hidden aspect-[4/5]">
              <img
                src="/landing/nicolas-dojo.jpg"
                alt="Nicolas Le Berre assis en méditation dans le dojo"
                loading="lazy"
                className="h-full w-full object-cover object-center"
              />
            </div>
            <span className="absolute -bottom-10 right-0 translate-x-[40%] kanji text-[7rem] sm:text-[9rem] leading-none text-ink/[0.08] select-none" aria-hidden="true">
              道
            </span>
          </figure>

          <div className="lg:col-span-6 lg:col-start-7" data-reveal style={{ ['--reveal-delay' as string]: '150ms' }}>
            <SectionHeading index="04" eyebrow="Votre enseignant" title="Nicolas Le Berre" />
            <div className="mt-8 space-y-5 text-[15px] leading-relaxed text-ink/75">
              <p>
                Pratiquant de longue date des arts martiaux japonais, Nicolas puise dans la sagesse zen inhérente aux{' '}
                <em>Budō</em> (武道) comme dans l'étude de techniques plus contemporaines de neurophysiologie.
              </p>
              <p>
                Il vous accompagne dans votre quête d'une énergie stable et puissante, d'une paix intérieure durable et
                d'une vie authentique, alignée avec vos valeurs et vos aspirations profondes. Le Dōjō de l'Esprit est
                plus qu'un lieu de pratique : c'est un chemin, où chaque séance vous rapproche un peu plus de votre
                essence véritable.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm font-medium">
              <ExternalLink href={SKOOL_URL}>Communauté Skool · The Elastic Warrior</ExternalLink>
              <ExternalLink href={FACEBOOK_URL}>Facebook · Be Your Zen Self</ExternalLink>
              <ExternalLink href={PRIMELIGHT_URL}>La méditation PrimeLight</ExternalLink>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Appel final                                                         */}
      {/* ------------------------------------------------------------------ */}
      <section id="contact" className="scroll-mt-20 relative bg-ink text-paper overflow-hidden">
        <img
          src="/landing/kick-sunset.jpg"
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover object-[center_40%] opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/60 to-ink" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-28 sm:py-40 text-center" data-reveal>
          <Eyebrow className="text-sage-200">Le premier pas</Eyebrow>
          <h2 className="mt-6 font-display font-light text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            Au plaisir de vous accueillir{' '}
            <br className="hidden sm:block" />
            les mardis soirs.
          </h2>
          <p className="mt-6 text-paper/70 max-w-xl mx-auto text-[15px] sm:text-base leading-relaxed">
            Informations et réservations par SMS ou WhatsApp au {PHONE_DISPLAY}.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={btnPaper}>
              <MessageCircle size={16} strokeWidth={1.75} />
              Réserver par WhatsApp
            </a>
            <a href={WHATSAPP_GROUP_URL} target="_blank" rel="noopener noreferrer" className={btnGhostPaper}>
              Rejoindre le groupe WhatsApp
              <ArrowUpRight size={16} strokeWidth={1.75} />
            </a>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Pied de page                                                        */}
      {/* ------------------------------------------------------------------ */}
      <footer className="mx-auto max-w-7xl px-5 sm:px-8 py-10 flex flex-col sm:flex-row gap-6 sm:items-center justify-between text-sm text-ink/60">
        <div className="flex items-center gap-3">
          <img src="/landing/logo-enso.png" alt="" className="h-8 w-8 object-contain" />
          <span>
            <span className="font-display text-lg text-ink">Dōjō de l'Esprit</span> · Nicolas Le Berre ·{' '}
            {new Date().getFullYear()}
          </span>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <a href={SMS_URL} className="hover:text-ink transition-colors">{PHONE_DISPLAY}</a>
          <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="hover:text-ink transition-colors">Facebook</a>
          <a href={SKOOL_URL} target="_blank" rel="noopener noreferrer" className="hover:text-ink transition-colors">The Elastic Warrior</a>
          <a href={LIEU_URL} target="_blank" rel="noopener noreferrer" className="hover:text-ink transition-colors">Parenthèse Holistique</a>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
